import TypeFilter from "@components/Pricing/RegionalInsuranceList/TypeFilter";
import RegionTreeSelector from "@components/Region/RegionTreeSelector";
import {useTranslation} from "ni18n";
import React, {useMemo, useState} from "react";
import useMediaQuery from "src/useMediaQuery";

import hardcodedVirtualRegions from "../../../../constants/hardcodedVirtualRegions";
import {PriceRegionWithPrice} from "../../../../constants/regionsByPricingRegionName";
import {getPricesByRegionSlug} from "../../../../utils/getPricesByRegionSlug";
import RegionFilter from "./RegionFilter";
import SearchFilter from "./SearchFilter";
import SectionGroupList from "./SectionGroupList";
import {getFilteredGroupList, useFilters} from "./utils";
import {useUserSelectedLocation} from "../../../../utils/browser-storage/userSelectedLocation";

type Props = {
  allPrices: PriceRegionWithPrice[];
};

const RegionalInsuranceList: React.FC<Props> = ({allPrices}) => {
  const i18n = useTranslation();
  const isMd = useMediaQuery("md");
  const {regionSlug: selectedRegion} = useUserSelectedLocation();

  // Prices for selected Region
  const prices = useMemo(
    () => getPricesByRegionSlug(allPrices, selectedRegion),
    [allPrices, selectedRegion],
  );

  const types = React.useRef(prices.map(p => p.typ).distinct()).current;
  const {filters, toggleFilter} = useFilters(types);
  const [searchText, setSearchText] = useState("");

  const pricesGroupedByType = useMemo(
    () =>
      getFilteredGroupList(
        prices,
        filters,
        searchText,
        hardcodedVirtualRegions.some(({slug}) => slug === selectedRegion),
      ),
    [prices, filters, searchText, selectedRegion],
  );

  const numberOfResults = Object.values(pricesGroupedByType).reduce(
    (total, current) => total + current.length,
    0,
  );

  return (
    <section aria-label={i18n.t("Our Prices")} className="pt20 pb26 bg-gray50 contrast-tbb">
      <div className="maxw70 ma ph8-xl ph4-sm">
        <h2 className="tac mb4 fs48 fs28-sm lh12 gray800">{i18n.t("Clear costs, great care")}</h2>
        <div className="mb12">
          <RegionFilter>
            <div className="df jcc">
              <RegionTreeSelector buttonVariant="secondary" />
            </div>
          </RegionFilter>
        </div>
        <div className="df jcc-md">
          <TypeFilter filters={filters} toggleFilter={toggleFilter} />
          <SearchFilter isExpanded={isMd} searchText={searchText} setSearchText={setSearchText} />
        </div>
        <span className="visually-hidden" aria-live="polite">
          {numberOfResults
            ? i18n.t("{{count}} results found", {
                count: numberOfResults,
              })
            : i18n.t("No results found for selected criteria")}
        </span>
        <SectionGroupList pricesGroupedByType={pricesGroupedByType} />
      </div>
    </section>
  );
};

export default React.memo(RegionalInsuranceList);
